import { useState, type ReactNode } from 'react';
import {
  IconActivity, IconArrowLeft, IconArrowRight, IconArrowsExchange, IconBell, IconBox, IconChartBar, IconCheck, IconChecklist, IconChevronDown, IconChevronUp, IconCopy,
  IconCurrentLocation, IconDots, IconDownload, IconEdit, IconExternalLink, IconEye, IconFileText, IconHistory, IconInfoCircle, IconLoader, IconMapPin, IconMessage, IconMessages,
  IconPackage, IconPencil, IconPhone, IconPhoto, IconPlus, IconReceipt, IconRoute, IconSend, IconSwitchHorizontal, IconTool, IconTruck,
  IconTruckDelivery, IconUser, IconUsers,
} from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Page, PageHeader, useShell } from '@hana/shell';
import { IpadTripDetail } from './IpadDetail';
import { useT } from '@hana/i18n';
import './i18n';
import { Button, Notes, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { TRIP_DETAIL, confirmAllLegs, confirmLeg, filterActivity, pendingLegs, phasesWithPhoto, type ActCat, type Metric, type Tone } from './tripDetail';
import s from './tripDetail.module.css';

// Mint-TMS-Trip (desktop 1536) · lệnh vận tải TRF…: kế hoạch chuyến, chỉ số, chặng & ảnh, ghi chú, chứng từ, hoạt động, chi phí.
// Chỉ đọc + thao tác giả lập trên dữ liệu mẫu (toast; xác nhận chặng giữ trong bộ nhớ). Không có tiền. Bản đồ / GPS: khung giữ chỗ.
// Chưa làm: tab GPS, mở rộng Chứng từ / Chi phí chuyến (đang gập như board), "Xem thêm" của Hoạt động và Việc cần làm.

const TIPS = {
  plan: 'Sự kiện lấy từ cập nhật của tài xế trên HANA Mov và GPS (adsun-proxy). Giờ hẹn theo điểm dừng OpStopsJson; giờ thực tế / dự kiến từ GPS và check-in. Trễ quá 15 phút thì tô vàng.',
  idx: 'Operating Index tính khi Finish: đúng giờ, thời gian tại kho, km rỗng, nhiên liệu so chuẩn, đủ ảnh và chứng từ. Vạch dọc trên thanh = chuẩn.',
  legs: 'Chạy có hàng / rỗng / không mooc · cắt mooc (DROP) · móc mooc (HOOK) · neo xe. Ảnh theo pha: nén + watermark giờ, GPS.',
  notes: 'Ghi chú nội bộ của lệnh; gõ @ để nhắc người (người được nhắc có việc trong Việc & Duyệt). Ghi chú cho tài xế nằm ngay tại từng điểm trên Kế hoạch chuyến, sửa ở Sửa điểm.',
  docs: 'Phiếu EIR, lệnh cấp rỗng, giấy vận tải, booking (TblFileLibrary). Gửi cho tài xế qua app HANA Mov hoặc Zalo, ghi nhật ký.',
  act: 'Một timeline: nhật ký lệnh (TblJobEventLog + TblEntityAudit: bước, chặng, VETC, chi phí, tệp, ghi chú, dữ liệu đổi), cập nhật tài xế HANA Mov, GPS và chat nhóm job HANA Hei. Bấm một dòng để xem chi tiết sự kiện.',
  cost: 'Phụ thu, cầu đường VETC, phí lưu mooc / neo xe, phân bổ chuyến ghép. Số tiền chỉ hiện khi có quyền xem tiền. Đề nghị thanh toán, biên nhận dầu, cầu đường từ app tài xế nằm ở màn Chi phí (theo quyền tài chính).',
  todo: 'Việc sinh từ dữ liệu lệnh: thiếu ảnh pha, chờ EIR, chặng GPS chờ xác nhận, xe trễ, phụ thu. Hạn tính theo giờ làm việc (WorkCalendar).',
  info: 'Job liên kết, khách, container (kiểm ISO 6346), booking, kho và hạn chế xe của kho. Mã có nút chép.',
  pic: 'Hai nguồn, gộp và bỏ trùng: (1) theo khách của job: Sale, CS, Sale Asst, Kế toán (CUSDATA); (2) được giao trên chuyến theo TblOrgJD: Điều độ (TRD-DSP) và tài xế. Chưa có người hiện Chưa giao, nút Giao chỉ hiện khi có quyền. Avatar từ TblStaff.ImageProfile. Đổi xe / tài xế ở Lưới điều xe.',
  drv: 'Gọi qua tổng đài (ghi nhật ký) hoặc chat 1-1 trong HANA Hei. Không hiện số điện thoại cá nhân.',
  cutoff: 'Luật hạn: CloseCY 24/Oct/2026 20:00 − 5h (luật SOP) rồi kẹp lùi vào ca làm việc: T7 làm đến 12:00 → 24/Oct/2026 12:00 · WorkCalendar (tblClientInfo)',
} as const;

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const TONE: Record<Tone, string> = { ok: s.pOk!, warn: s.pWarn!, err: s.pErr!, info: s.pInfo!, mute: '' };
const Pill = ({ tone = 'mute', children }: { tone?: Tone; children: ReactNode }) => <span className={`${s.pill} ${TONE[tone]}`}>{children}</span>;
const Info = ({ tip }: { tip: string }) => (
  <span className={s.tip} role="img" aria-label={`Giải thích: ${tip}`} title={tip}>
    <IconInfoCircle size={14} stroke={1.9} aria-hidden="true" />
  </span>
);

function Card({ id, title, icon, tip, pills, actions, open = true, onToggle, bodyCls, children }: { bodyCls?: string; id: string; title: string; icon: ReactNode; tip: string; pills?: ReactNode; actions?: ReactNode; open?: boolean; onToggle: () => void; children?: ReactNode }) {
  return (
    <section aria-label={title} className={s.card}>
      <div className={s.ch}>
        <span className={s.chIco} aria-hidden="true">{icon}</span>
        <h2 className={s.h2}>{title}</h2>
        <Info tip={tip} />
        {pills}
        <div className={s.chAct}>
          {actions}
          <button type="button" className={s.fold} aria-label={`Mở hoặc gập ${title}`} aria-expanded={open} aria-controls={`b-${id}`} onClick={onToggle}>
            {open ? <IconChevronUp size={18} stroke={1.9} aria-hidden="true" /> : <IconChevronDown size={18} stroke={1.9} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {open && <div id={`b-${id}`} className={`${s.cb} ${bodyCls ?? ''}`}>{children}</div>}
    </section>
  );
}

const CHIP_ICON = { truck: IconTruck, mooc: IconBox, cont: IconPackage, km: IconRoute, driver: IconUser } as const;
const BAR: Record<Metric['tone'], string> = { blue: 'var(--d-blue)', amber: 'var(--d-amber)', green: 'var(--d-green)' };
const FILTERS: ReadonlyArray<{ key: 'all' | ActCat; label: string }> = [
  { key: 'all', label: 'Tất cả' },
  { key: 'driver', label: 'Tài xế' },
  { key: 'dispatch', label: 'Điều độ' },
  { key: 'system', label: 'Hệ thống' },
  { key: 'gps', label: 'GPS' },
  { key: 'chat', label: 'Chat nhóm' },
];
const TAG_ICON: Record<string, typeof IconRoute> = { VETC: IconReceipt, Chặng: IconRoute, Ảnh: IconPhoto, Chat: IconMessage, Bước: IconCheck, Tệp: IconFileText };

function Gauge({ value, weekAgo }: { value: number; weekAgo: number }) {
  return (
    <svg role="img" aria-label={`Điểm vận hành ${value} trên 100, tuần trước ${weekAgo}`} width="150" height="82" viewBox="0 0 190 104" className={s.gauge}>
      <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--line)" strokeWidth="14" strokeLinecap="round" />
      <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--d-blue)" strokeWidth="14" strokeLinecap="round" strokeDasharray="251.3 251.3" strokeDashoffset={(251.3 * (1 - value / 100)).toFixed(1)} />
      <text x="95" y="84" textAnchor="middle" fontSize="30" fontWeight="550" fill="var(--tx)" fontFamily="inherit">{value}</text>
    </svg>
  );
}

function Detail({ id }: { id: string }) {
  const T = useT();
  const base = TRIP_DETAIL[id]!;
  const toast = useToast();
  const navigate = useNavigate();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const back = () => void navigate({ to: '/ezy/tms' });
  const [tab, setTab] = useState<'route' | 'gps'>('route');
  const [dense, setDense] = useState<'std' | 'compact'>('std');
  const [open, setOpen] = useState<Record<string, boolean>>({ plan: true, idx: true, legs: true, docs: false, act: true, cost: false, todo: true, info: true, pic: true, drv: true });
  const [legs, setLegs] = useState(base.legs);
  const [cat, setCat] = useState<'all' | ActCat>('all');
  const tg = (k: string) => () => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const pend = pendingLegs({ legs });
  const photoN = phasesWithPhoto(base);
  const acts = filterActivity(base.activity, cat);
  const confirm = (n: string) => {
    const prev = legs;
    setLegs(confirmLeg(legs, n));
    toast({ msg: `Đã xác nhận chặng ${n}`, undo: () => setLegs(prev) });
  };
  const confirmAll = () => {
    const prev = legs;
    setLegs(confirmAllLegs(legs));
    toast({ msg: `Đã xác nhận ${pend} chặng`, undo: () => setLegs(prev) });
  };
  const missing = base.missing;

  return (
    <>
      <PageHeader findPad
        title={base.id}
        findHint={false}
        lead={
          <button type="button" className={s.back} aria-label="Về điều độ" title="Về điều độ" onClick={back}>
            <IconArrowLeft size={18} aria-hidden="true" />
          </button>
        }
        meta={
          <span className={s.hChips}>
            <span className={`${s.hChip} ${s.hBlue}`}><i style={{ background: 'var(--d-blue)' }} />{base.status}</span>
            <span className={`${s.hChip} ${s.hBlue}`}><i style={{ background: 'var(--d-blue)' }} />50E13970 · {T('tms.td.ownTruck')}</span>
            {missing > 0 && <span className={`${s.hChip} ${s.hWarn}`}><i style={{ background: 'var(--d-amber)' }} />{`thiếu ${missing} mục Finish`}</span>}
          </span>
        }
        actions={
          <>
            <Segmented kind="toggle" label="Mật độ" value={dense} onChange={setDense} items={[{ key: 'std', label: 'Chuẩn' }, { key: 'compact', label: 'Gọn' }]} />
            <Button variant="secondary" icon={<IconSwitchHorizontal size={16} />} onClick={noWrite}>{T('tms.td.sameTruck')}</Button>
            <Button variant="primary" icon={<IconCheck size={16} />} onClick={noWrite}>{T('tms.td.finish')}</Button>
            <button type="button" className={s.more} aria-label="Thêm thao tác" title="Thêm thao tác" onClick={noWrite}>
              <IconDots size={18} aria-hidden="true" />
            </button>
          </>
        }
      />
      <div className={`${s.page} ${dense === 'compact' ? s.compact : ''}`}>
        <div className={s.sub}>{base.sub}</div>
        <Segmented label="Mục" value={tab} onChange={setTab} items={[{ key: 'route', label: 'Lộ trình & cont', n: 6 }, { key: 'gps', label: 'GPS' }]} />
        <div className={s.grid}>
          <div className={s.col}>
            <section aria-label="Kế hoạch chuyến" className={`${s.card} ${s.plan}`}>
              <div className={s.planHd}>
                <span className={s.chIco} aria-hidden="true"><IconTruckDelivery size={18} stroke={1.9} /></span>
                <h2 className={s.h2}>{T('tms.td.plan')}</h2>
                <Info tip={TIPS.plan} />
                <span className={s.planN}>{base.planCount.replace(' điểm', '')} <span>{T('tms.td.stops')}</span></span>
              </div>
              <div className={s.planTop}>
                <div className={s.planL}>
                  <div className={s.prow}>
                    <span className={s.pIco} role="img" aria-label="Trạng thái"><IconLoader size={16} stroke={1.8} /></span>
                    <span className={s.stBadge}><i />{base.status}</span>
                  </div>
                  <div className={s.prow}>
                    <span className={s.pIco} role="img" aria-label="Gần nhất"><IconHistory size={16} stroke={1.8} /></span>
                    <span className={s.ptx}>{base.last}</span>
                  </div>
                  <div className={s.prow}>
                    <span className={s.pIco} role="img" aria-label="Kế tiếp"><IconArrowRight size={16} stroke={1.8} /></span>
                    <span className={s.ptx}>{base.next}</span>
                    <button type="button" className={s.soft} onClick={noWrite}><IconBell size={14} stroke={2} aria-hidden="true" />{T('tms.td.remind')}</button>
                  </div>
                </div>
                <div className={s.planR}>
                  <b>{T('tms.td.runTime')}</b>
                  <span className={s.planV}>{base.runtime}</span>
                  <span className={s.note}>{base.runtimeSub}</span>
                </div>
              </div>
              <div className={s.hr} />
              <div className={s.stopsWrap}>
                <div className={s.stops} role="list" aria-label="Điểm tuyến theo thứ tự">
                  {base.stops.map((p) => (
                    <div key={p.key} role="listitem" className={s.stop}>
                      <div className={s.pinCol}>
                        <span className={`${s.pin} ${p.tone === 'done' ? s.pinDone : s.pinNext}`} aria-hidden="true"><IconMapPin size={18} stroke={1.9} /></span>
                        {p.line && <span aria-hidden="true" className={s.line} style={{ borderColor: p.line === 'done' ? 'var(--d-green)' : 'var(--d-blue)' }} />}
                      </div>
                      <div className={s.stopBody}>
                        <div className={s.note}>{p.label}</div>
                        <button type="button" className={s.place} title="Sửa điểm" aria-label={`Sửa điểm ${p.label}, ghi chú cho tài xế`} onClick={noWrite}>{p.place}</button>
                        <div className={`${s.time} ${p.late ? s.timeLate : ''}`}>
                          <span><b>{p.time}</b> <span>({p.kind})</span></span>
                          {p.shift && <span className={s.shift}>{p.shift}</span>}
                        </div>
                        {(p.code || p.hours) && <div className={`${s.note} ${s.ell}`}>{p.code ?? p.hours}</div>}
                        <div className={s.dn} role="note" aria-label={`Ghi chú cho tài xế tại ${p.label}`} title="Ghi chú cho tài xế · sửa ở Sửa điểm">
                          <IconPencil size={13} stroke={1.9} aria-hidden="true" />
                          <span>{p.note}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className={s.stopAct}>
                  <Button variant="primary" size="sm" className={s.b34} icon={<IconCurrentLocation size={15} />} onClick={noWrite}>{T('tms.td.trackGps')}</Button>
                  <Button variant="soft" size="sm" className={s.b34} icon={<IconPlus size={16} />} onClick={noWrite}>{T('tms.td.mergeStop')}</Button>
                </div>
              </div>
              <div className={s.legend}>
                <span className={s.sw} />
                {T('tms.lateVsPlan')}
              </div>
              <div className={s.chips} role="group" aria-label="Xe, mooc, tài xế, km">
                {base.chips.map((c) => {
                  const Ico = CHIP_ICON[c.icon];
                  return c.icon === 'driver' ? (
                    <span key={c.icon} className={`${s.cchip} ${s.cchipAv}`}>
                      <span className={s.av22}>HA</span>
                      {c.text}
                    </span>
                  ) : (
                    <span key={c.icon} className={s.cchip}>
                      <Ico size={14} stroke={1.9} aria-hidden="true" />
                      {c.text}
                    </span>
                  );
                })}
              </div>
              <div className={s.cut}>
                <span className={`${s.pill} ${TONE[base.cutoff.tone]}`}>{`${base.cutoff.tag} · `}<b>{base.cutoff.ok}</b></span>
                <span className={s.cutTx}>{base.cutoff.text}</span>
                <span className={s.tip} role="img" aria-label={TIPS.cutoff} title={TIPS.cutoff}><IconInfoCircle size={15} stroke={1.9} aria-hidden="true" /></span>
                <button type="button" className={`${s.soft} ${s.soft30}`} onClick={noWrite}><IconSend size={16} stroke={1.9} aria-hidden="true" />{T('tms.td.earlyDrop')}</button>
              </div>
            </section>

            <Card id="idx" title={T('tms.td.idx')} icon={<IconChartBar size={18} stroke={1.9} />} tip={TIPS.idx} pills={<Pill>{T('tms.td.calcOnFinish')}</Pill>} open={open.idx} onToggle={tg('idx')}>
              <div className={s.idxTop}>
                <div className={s.gaugeBox}>
                  <Gauge value={base.score.value} weekAgo={base.score.weekAgo} />
                  <span className={s.note}>{`điểm /100 · tuần ${base.score.weekAgo}`}</span>
                </div>
                <div className={s.metrics}>
                  {base.metrics.map((m) => (
                    <div key={m.label} className={s.metric}>
                      <div className={s.note}>{m.label}</div>
                      <div className={s.mv}>
                        <b className={m.hot === 'warn' ? s.warnTx : m.hot === 'ok' ? s.okTx : ''}>{m.value}</b>
                        <span className={s.note}>{m.unit}</span>
                      </div>
                      <div className={s.bar}>
                        <div style={{ width: `${m.pct}%`, background: BAR[m.tone] }} />
                        {m.mark != null && <span className={s.mark} style={{ left: `${m.mark}%` }} />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={s.flags}>
                {base.flags.map((f) => <Pill key={f} tone="warn">{f}</Pill>)}
              </div>
            </Card>

            <Card
              id="legs"
              title="Chặng & ảnh theo pha"
              icon={<IconRoute size={18} stroke={1.9} />}
              tip={TIPS.legs}
              pills={<><Pill>{`${legs.length} chặng`}</Pill>{pend > 0 && <Pill tone="warn">{`${pend} chờ xác nhận`}</Pill>}</>}
              actions={<Button variant="secondary" size="sm" className={s.b30} icon={<IconPlus size={16} />} onClick={noWrite}>{T('tms.td.logLeg')}</Button>}
              open={open.legs}
              onToggle={tg('legs')}
            >
              {pend > 0 && (
                <div className={s.pendBar}>
                  <b>{`${pend} chặng GPS đề xuất chờ xác nhận`}</b>
                  <span>{base.legsPendingMsg}</span>
                  <button type="button" className={s.white} onClick={confirmAll}><IconChecklist size={16} aria-hidden="true" />{T('tms.td.confirmAll')}</button>
                </div>
              )}
              <div className={`${s.lrow} ${s.lhead}`}>
                <span>#</span><span>{T('tms.td.thEvent')}</span><span>{T('tms.td.thFromTo')}</span><span>{T('tms.td.thTime')}</span><span className={s.kmH}>Km</span><span>{T('tms.td.thSource')}</span>
              </div>
              {legs.map((l) => (
                <div key={l.n} className={`${s.lrow} ${s.lbody}`}>
                  <b>{l.n}</b>
                  <span className={s.lk}>{l.kind}</span>
                  <span className={s.lr}>{l.route}</span>
                  <span>{l.time}</span>
                  <span className={s.km}>{l.km}</span>
                  <span className={s.src}>
                    <Pill tone={l.source === 'GPS đề xuất' ? 'warn' : 'mute'}>{l.source}</Pill>
                  </span>
                  <span className={s.lAct}>
                    {l.pending && (
                      <button type="button" className={s.sm} aria-label={`Xác nhận chặng ${l.n}`} onClick={() => confirm(l.n)}>
                        <IconCheck size={16} aria-hidden="true" />{T('tms.confirm')}
                      </button>
                    )}
                  </span>
                </div>
              ))}
              <div className={s.photoHd}>
                <b>{T('tms.td.phasePhotos')}</b>
                <Pill tone={photoN < base.photos.length ? 'warn' : 'ok'}>{`${photoN}/${base.photos.length} pha có ảnh`}</Pill>
              </div>
              <div className={s.photos}>
                {base.photos.map((p) => (
                  <div key={p.key} className={`${s.photo} ${p.n === 0 ? s.photoMiss : ''}`}>
                    <div className={s.photoTop}>
                      <IconPhoto size={16} stroke={1.9} aria-hidden="true" />
                      <span className={s.photoName}>{p.label}</span>
                      <span className={s.cnt}><i style={{ background: p.n === 0 ? 'var(--badge)' : 'var(--d-green)' }} /><b>{p.n}</b></span>
                    </div>
                    <div className={s.photoAct}>
                      <button type="button" className={s.softS} aria-label={`Thêm ảnh pha ${p.label}`} onClick={noWrite}><IconPlus size={14} stroke={2} aria-hidden="true" />{T('tms.td.photo')}</button>
                      {p.n > 0 && (
                        <button type="button" className={s.ctlS} aria-label={`Chuyển ảnh pha ${p.label} sang pha khác`} onClick={noWrite}><IconArrowsExchange size={14} stroke={2} aria-hidden="true" />{T('tms.td.movePhase')}</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card
              id="docs"
              title="Chứng từ"
              icon={<IconFileText size={18} stroke={1.9} />}
              tip={TIPS.docs}
              pills={<><Pill>{`${base.docs.have}/${base.docs.total}`}</Pill>{base.docs.have < base.docs.total && <Pill tone="err">{`${base.docs.total - base.docs.have} thiếu`}</Pill>}</>}
              actions={<Button variant="secondary" size="sm" className={s.b30} icon={<IconSend size={16} />} onClick={noWrite}>{T('tms.td.sendAll')}</Button>}
              open={open.docs}
              onToggle={tg('docs')}
            />

            <Card
              id="act"
              title="Hoạt động"
              icon={<IconActivity size={18} stroke={1.9} />}
              tip={TIPS.act}
              pills={<Pill>{`${base.activityTotal} mục`}</Pill>}
              actions={<Button variant="secondary" size="sm" className={s.b30} icon={<IconDownload size={14} />} aria-label="Xuất nhật ký CSV" onClick={noWrite}>{T('tms.td.exportCsv')}</Button>}
              open={open.act}
              onToggle={tg('act')}
            >
              <div className={s.seg} role="group" aria-label="Lọc hoạt động">
                {FILTERS.map((f) => (
                  <button key={f.key} type="button" aria-pressed={cat === f.key} className={s.segB} onClick={() => setCat(f.key)}>
                    {f.label}
                    <span className={s.segN}>{base.activityCounts[f.key]}</span>
                  </button>
                ))}
              </div>
              {acts.map((a) => {
                const Ico = TAG_ICON[a.tag] ?? IconRoute;
                return (
                  <div key={a.text} className={s.arowW}>
                    <button type="button" className={s.arow} aria-label={`Xem chi tiết sự kiện: ${a.text}`} onClick={noWrite}>
                      <span className={`${s.aIco} ${a.tone === 'teal' ? s.aTeal : ''}`}><Ico size={15} stroke={1.9} aria-hidden="true" /></span>
                      <span className={s.aTag}>{a.tag}</span>
                      <span className={s.aTx}>{a.text}</span>
                      <span className={`${s.av22} ${a.ini === 'HA' ? '' : a.ini ? s.avMute : s.avBlue}`}>{a.ini ?? <IconUser size={13} aria-hidden="true" />}</span>
                      <span className={s.aWho}>{a.who}</span>
                      <span className={s.aAt}>{a.at}</span>
                    </button>
                  </div>
                );
              })}
              <div className={s.more2}>
                <button type="button" className={s.ctlBtn} onClick={noWrite}><IconChevronDown size={16} aria-hidden="true" />{`Xem thêm ${base.activityTotal - base.activity.length}`}</button>
                <a className={s.lnk} href="/hei/" onClick={(e) => { e.preventDefault(); noWrite(); }}><IconMessages size={16} aria-hidden="true" />{T('tms.td.askJob')}</a>
              </div>
            </Card>

            <Card
              id="cost"
              title="Chi phí chuyến"
              icon={<IconReceipt size={18} stroke={1.9} />}
              tip={TIPS.cost}
              pills={<Pill tone="warn">{T('tms.td.needConfirm')}</Pill>}
              actions={
                <a className={s.out} aria-label="Chi phí chuyến · Chi phí theo mảng Trucking" href="/ezy/tms" onClick={(e) => { e.preventDefault(); noWrite(); }}>
                  <IconExternalLink size={16} aria-hidden="true" />{T('tms.costTrucking')}
                </a>
              }
              open={open.cost}
              onToggle={tg('cost')}
            />
          </div>

          <div className={`${s.col} ${s.colR}`}>
            <Card
              id="todo"
              title="Việc cần làm"
              icon={<IconChecklist size={18} stroke={1.9} />}
              tip={TIPS.todo}
              pills={<Pill tone="warn">{base.todosTotal}</Pill>}
              open={open.todo}
              onToggle={tg('todo')}
            >
              <div className={s.todos}>
                {base.todos.map((t) => (
                  <div key={t.text} className={s.todo}>
                    <button type="button" className={s.chk} aria-label={`Xong: ${t.text}`} onClick={noWrite}><span /></button>
                    <span className={s.todoTx}>
                      <span className={s.todoT}>{t.text}</span>
                      <span className={s.todoM}>
                        <span className={s.av18}>{t.ini}</span>
                        <span>{`${t.who} ·`}</span>
                        <span className={t.dueTone === 'err' ? s.errTx : s.warnTx}>{t.due}</span>
                      </span>
                    </span>
                    <button type="button" className={s.sq} aria-label={t.action} title={t.action} onClick={noWrite}>
                      {t.action === 'Xác nhận' ? <IconCheck size={16} aria-hidden="true" /> : t.action === 'Ghi lý do' ? <IconTool size={16} aria-hidden="true" /> : <IconBell size={16} aria-hidden="true" />}
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className={s.moreBtn} onClick={noWrite}><IconEye size={16} aria-hidden="true" />{`Xem thêm ${base.todosTotal - base.todos.length}`}</button>
            </Card>

            <Card id="info" title="Thông tin chính" icon={<IconFileText size={18} stroke={1.9} />} tip={TIPS.info} open={open.info} onToggle={tg('info')}>
              {base.info.map((r) => (
                <div key={r.k} className={s.kv}>
                  <span className={s.k}>{r.k}</span>
                  {r.link ? (
                    <Link to="/ezy/jobs/$jobId" params={{ jobId: r.v }} className={s.vLink}>{r.v} ↗</Link>
                  ) : (
                    <span className={s.v}>{r.v}</span>
                  )}
                  {r.edit && <button type="button" className={s.sq28} aria-label="Sửa container và seal" onClick={noWrite}><IconEdit size={15} aria-hidden="true" /></button>}
                  {r.copy && (
                    <button type="button" className={s.sq28} aria-label={`Chép ${r.k} ${r.v}`} onClick={() => { void navigator.clipboard?.writeText(r.v.split(' · ')[0] ?? r.v); toast({ msg: `Đã chép ${r.k}` }); }}>
                      <IconCopy size={15} aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
              <div className={`${s.kv} ${s.kvTop}`}>
                <span className={s.k}>{T('tms.td.depotLimit')}</span>
                <div className={s.restr}>
                  {base.restrictions.map((r) => <Pill key={r.text} tone={r.tone}>{r.text}</Pill>)}
                </div>
              </div>
            </Card>

            <Notes
              scope="trf"
              id={id}
              tip={TIPS.notes}
              notes={base.notes.map((n, i) => ({ id: `${id}-n${i}`, text: n.text, kind: n.kind, by: n.by, at: n.at, pinned: n.pinned, stop: true }))}
              onAdd={noWrite}
            />

            <Card id="pic" title="Người phụ trách" icon={<IconUsers size={18} stroke={1.9} />} tip={TIPS.pic} open={open.pic} onToggle={tg('pic')}>
              {base.pics.map((p) => (
                <div key={p.role} className={s.pic}>
                  <span className={`${s.av32} ${p.tone === 'violet' ? '' : p.tone === 'blue' ? s.avBlue : s.avMute}`}>{p.tone === 'blue' ? <IconUser size={16} aria-hidden="true" /> : p.ini}</span>
                  <span className={s.picTx}>
                    <b className={p.none ? s.mute : ''}>{p.who}</b>
                    <span className={s.note}>{p.role}</span>
                  </span>
                  {p.none && <button type="button" className={s.give} aria-label={`Giao ${p.role}`} onClick={noWrite}>Giao</button>}
                </div>
              ))}
            </Card>

            <Card id="drv" bodyCls={s.cbDrv} title="Liên hệ tài xế" icon={<IconPhone size={18} stroke={1.9} />} tip={TIPS.drv} open={open.drv} onToggle={tg('drv')}>
              <div className={s.drv}>
                <span className={s.av40}>{base.driver.ini}</span>
                <span className={s.picTx}>
                  <b>{base.driver.name}</b>
                  <span className={s.note}>{base.driver.status}</span>
                </span>
              </div>
              <div className={s.drvAct}>
                <button type="button" className={s.act} onClick={noWrite}><IconPhone size={16} aria-hidden="true" />{T('tms.td.call')}</button>
                <a className={s.act} href="/hei/" onClick={(e) => { e.preventDefault(); noWrite(); }}><IconMessage size={16} aria-hidden="true" />Chat Hei</a>
                <button type="button" className={s.act34} aria-label="Lệnh dạng chữ gửi tài xế" title="Lệnh dạng chữ gửi tài xế" onClick={noWrite}><IconFileText size={16} aria-hidden="true" /></button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}

export function TripDetailPage() {
  const T = useT();
  const { tripId } = useParams({ strict: false }) as { tripId?: string };
  const navigate = useNavigate();
  const layout = useShell().layout;
  const { data, error, isLoading, isError, refetch } = useList('trip', { limit: 500 });
  const forced = readState();
  const trips = data?.items ?? [];
  const found = trips.find((t) => t.id === tripId);
  const has = !!tripId && !!TRIP_DETAIL[tripId];

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (!found || (!has && layout !== 'pad')) state = 'empty';

  const back = () => void navigate({ to: '/ezy/tms' });
  if (layout === 'pad' && state === 'ready') return <Page kind="detail"><IpadTripDetail id={tripId} onBack={back} /></Page>;
  if (state === 'ready' && has) return <Page><Detail id={tripId!} /></Page>;
  return (
    <Page>
      <PageHeader title={tripId ?? 'Lệnh vận tải'} actions={<Button variant="secondary" icon={<IconArrowLeft size={16} />} onClick={back}>{T('tms.td.back')}</Button>} />
      <div className={s.page}>
        <StateView
          state={state}
          icon={<IconTruckDelivery size={26} stroke={1.8} />}
          title={state === 'empty' ? (found ? `Chưa có chi tiết mẫu cho ${tripId}` : `Không tìm thấy lệnh ${tripId ?? ''}`) : undefined}
          action={state === 'error' ? <Button variant="secondary" onClick={() => void refetch()}>{T('tms.ov.retry')}</Button> : state === 'empty' ? <Button onClick={back}>{T('tms.td.back')}</Button> : undefined}
          skeleton={
            <div className={s.grid}>
              <div className={s.skel} style={{ height: 520 }}><Skeleton /></div>
              <div className={s.skel} style={{ height: 338 }}><Skeleton /></div>
            </div>
          }
        >
          {null}
        </StateView>
      </div>
    </Page>
  );
}

